<template>
  <div class="chat-dialog-container">
    <div class="connecting-status" v-if="!sendingUser || !onLine">{{ $t('Connecting') }}</div>
    <div class="message-box" ref="msgBox">
      <el-divider v-if="canLoadMore[receivingUser]" class="loadmore-divider"
        ><el-button type="text" size="mini" :loading="isloading" @click="loadmore">{{ $t('Load more') }}</el-button></el-divider
      >
      <div v-for="item in chatMsgList" :key="item.id" class="msg-item" :class="{ 'received-msg': isReceivedMsg(item) }">
        <img :src="item.msg || item.url" v-if="item.type === 'img' || item.contentsType === 'IMAGE'" class="img-style" @load="scrollToBottom" />

        <el-card :body-style="{ padding: '0px' }" v-else-if="item.type === 'file' || item.contentsType === 'FILE'" class="file-style">
          <div class="card-inner">
            <h2>{{ $t('File') }}</h2>
            <span>
              <h3>{{ item.filename }}</h3>
            </span>
            <div class="bottom clearfix">
              <span>{{ readablizeBytes(item.file_length) }}</span>
              <a :href="item.msg || item.url" :download="item.filename">{{ $t('Download') }}</a>
            </div>
          </div>
        </el-card>
        <div v-else-if="item.type === 'audio'">
          <audio :src="item.msg" controls></audio>
        </div>
        <div class="msg" v-else v-html="renderTxt(item.msg || item.data)"></div>
        <div class="time-style">{{ renderTime(item.time) }}</div>
      </div>
    </div>
    <div class="chat-funcs">
      <div class="chat-funcs__section">
        <ChatEmoji v-on:selectEmoji="selectEmoji" :inpMessage="sendingTxt" />
        <ChatUpPic />
        <ChatUpFile />
        <ChatHistory v-if="isLogin" />
      </div>
      <div class="chat-funcs__section" v-if="(isHttps && !isIE && showMedia) || (webIMDebug && showMedia)">
        <i class="iconfont iconphone" @click="audioCall"></i>
        <i class="iconfont iconvideo" @click="videoCall"></i>
      </div>
    </div>

    <div class="textarea-container">
      <el-input type="textarea" @keyup.enter.native="send" v-model="sendingTxt" ref="txtDom" :placeholder="$t('Please type something...')"></el-input>
    </div>

    <el-button type="primary" size="mini" class="send-btn" @click="send">{{ $t('Send') }}</el-button>
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { browser } from '@/utils/function'
import WebIM from '@/utils/webIM'
import config from '@/config'
import tools from '@/utils/tools'
import ChatEmoji from '@/components/chat/ChatEmoji'
import ChatUpPic from '@/components/chat/ChatUpPic'
import ChatUpFile from '@/components/chat/ChatUpFile'
import ChatHistory from '@/components/chat/ChatHistory'
import ChatMixin from '@/mixins/chat'
import { setTimeout } from 'timers';

export default {
  name: 'ChatDialog',
  mixins: [ChatMixin],
  components: {
    ChatUpPic,
    ChatUpFile,
    ChatEmoji,
    ChatHistory
  },

  data() {
    return {
      sendingTxt: '',
      webIMDebug: WebIM.config.isDebug,
      showMedia: WebIM.config.showMedia,
      isIE: browser.trident,
      isHttps: window.location.protocol === 'https:',
      isloading: false
    }
  },

  computed: {
    ...mapGetters(['isLogin']),
    ...mapState(['chatMsgList', 'receivingUser', 'sendingUser', 'receiver', 'needScrollToBottom', 'canLoadMore', 'onLine'])
  },

  watch: {
    chatMsgList: {
      handler() {
        if (this.needScrollToBottom) {
          this.scrollToBottom()
        }
      }
    },

    // 断线重连
    sendingUser: {
      handler(value) {
        let timer = null
        if (value === '' && this.isLogin) {
          this.$store.dispatch('chatLogout')
          timer = setInterval(() => {
            if (this.sendingUser) {
              clearInterval(timer)
            }
            this.$store.dispatch('chatLogin', this.$store.getters.imAccount)
          }, 3000)
        }
      }
    }
  },

  mounted() {
    this.scrollToBottom()
  },

  methods: {
    ...mapActions(['sendTxtMessage']),

    loadmore() {
      this.isloading = true
      const initialHeight = this.$refs.msgBox.scrollHeight
      const options = {
        queue: this.receivingUser.toLowerCase(),
        isGroup: false,
        count: config.chatFetchCount,
        success: res => {
          this.$store.commit('setLoadMoreArr', { imAccount: this.receivingUser, value: res.length === config.chatFetchCount })
          this.$store.commit('updateChatList', { res, needScrollToBottom: false })
          this.isloading = false
          this.$nextTick(() => {
            this.$refs.msgBox.scrollTop = this.$refs.msgBox.scrollHeight - initialHeight
          })
        },
        fail: () => {
          this.isloading = false
        }
      }
      WebIM.conn.fetchHistoryMessages(options)
    },

    selectEmoji(txt) {
      this.sendingTxt = txt
      this.$refs.txtDom.focus()
    },

    send: tools.throttle(function() {
      if (!this.sendingTxt.trim()) {
        return
      }

      if (!this.sendingUser) {
        this.$notify.error({
          message: this.$t('No network at the moment')
        })
        return
      }

      const payload = {
        chatType: 'singleChat',
        chatId: this.receivingUser,
        sendingTxt: this.sendingTxt,
        callback: () => {
          this.sendingTxt = ''
        }
      }
      this.sendTxtMessage(payload)
    }, 1000),

    scrollToBottom() {
      setTimeout(() => {
        this.$refs.msgBox.scrollTop = this.$refs.msgBox.scrollHeight
      })
    },

    audioCall() {
      this.$store.commit('toogleMediaChat')
      this.$store.commit('makeCall', this.receiver.name)

      const payload = {
        chatType: 'singleChat',
        chatId: this.receivingUser,
        sendingTxt: this.$t('Request audio call')
      }
      this.sendTxtMessage(payload)

      WebIM.rtcCall.caller = this.sendingUser
      WebIM.rtcCall.makeVoiceCall(this.receivingUser, null, true, true) // 用法同视频
    },

    videoCall() {
      this.$store.commit('toogleMediaChat')
      this.$store.commit('makeCall', this.receiver.name)

      const payload = {
        chatType: 'singleChat',
        chatId: this.receivingUser,
        sendingTxt: this.$t('Request video call')
      }
      this.sendTxtMessage(payload)
      setTimeout(() => {
        WebIM.rtcCall.caller = this.sendingUser
        WebIM.rtcCall.makeVideoCall(this.receivingUser) // 第三个参数为是否录制、第四个参数为是否合并，参数可以为空
      }, 500)
      
    }
  }
}
</script>
<style lang="stylus">
.chat-dialog-container
  position relative
  .connecting-status
    position absolute
    width 100%
    line-height 2
    font-size 12px
    text-align center
    background $color-black-35
    color $color-white
  .message-box
    border-bottom 1px solid $color-gray-cc
    width 100%
    height 400px
    overflow-y auto

  .msg-item
    margin 5px 0 0 0
    display flex
    flex-direction column
    align-items flex-end
    padding 10px

  .msg-item .msg
    background-color $color-black-65
    color $color-white
    margin-bottom 5px
    padding 8px 15px
    border-radius 4px
    border 1px solid $color-gray-e5
    max-width 500px
    word-break break-all

  .msg-item.received-msg
    align-items flex-start

  .msg-item.received-msg .msg
    background-color $color-white
    color $color-black

  .img-style
    max-width 500px
    margin-bottom 10px
    border-radius 4px
    box-shadow 0 2px 12px 0 $color-black-10

  .time-style
    font-size 12px
    color $color-black-45
    clear both

  .chat-funcs
    display flex
    justify-content space-between
    align-items center
    height 40px
    width 100%
    background-color $color-white

  .chat-funcs__section
    display flex

  .iconphone, .iconvideo
    font-size 25px
    margin-right 10px
    cursor pointer

  .textarea-container
    background-color $color-white
    height 90px
  .el-textarea__inner
    border none
    resize none
    width 90%
    height 85px

  .chat-container
    position relative

  .send-btn
    position absolute
    right 10px
    bottom 10px

  .file-style
    width 240px
    margin-bottom 10px
    font-size 13px

  .card-inner
    padding 10px 14px 14px

    h2
      border-bottom 1px solid $color-gray-e5
      text-align left
      font-size 14px
      line-height 1.5
      padding-bottom 5px

    h3
      max-width 100%
      font-size 12px
      height 20px
      line-height 22px
      font-weight 600
      text-overflow ellipsis
      overflow hidden
      white-space nowrap
      text-align left
      margin-top 8px
      margin-bottom 20px

    .bottom span
      color $color-black-45
      text-align left

    a
      color $color-black-45
      float right
      text-decoration none

    .el-dropdown-link
      cursor pointer
      color $color-blue-50

    .el-icon-arrow-down
      font-size 12px
  .loadmore-divider
    width 60%
    margin: 50px auto
    .el-divider__text
      background $color-gray-fb
      padding 0 24px
</style>
